import React, {useState} from 'react';

import PropTypes from 'prop-types';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  makeStyles,
} from '@material-ui/core';
import {ExpandMore, Search} from '@material-ui/icons';
import {green, red} from '@material-ui/core/colors';
import {ActionButton} from '@dishtech-react/core';
import axios from 'axios';
import {useDispatch, useSelector} from 'react-redux';
import ErrorResult from './error-result';
import AvailResult from './avail-result';
import {AVAIL_ENDPOINT} from '../../../../helpers/constants';
import {
  findAvailBySimpleSearchResults,
  handleResultsError,
  setLoading,
} from '../../../../redux/actions/results-actions';
import {serviceConfig} from '../../../../../config/service-config';

const useAvailRowStyles = makeStyles(theme => ({
  baseChip: {
    padding: theme.spacing(0.5),
    fontSize: theme.spacing(2),
  },
  newAvailChip: {
    color: 'white',
    backgroundColor: green[400],
  },

  availHasErrorChip: {
    color: 'white',
    backgroundColor: red[400],
  },

  resultRowSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    justifyContent: 'left',
  },
  actionsWrapper: {
    alignItems: 'center',
    background: 'lightgray',
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(5),
  },
  actionButtonsWrapper: {
    gap: theme.spacing(1),
    display: 'flex',
    height: '100%',
  },
  actionsLoadingSpinner: {
    width: `${theme.spacing(3)}px !important`,
    height: `${theme.spacing(3)}px !important`,
  },
  relatedErrorWrapper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderLeft: '5px solid red',
    border: '1px solid red',
    borderRadius: '2px',
  },
  availStatusSection: {
    width: '20%',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'right',
    gap: theme.spacing(0.5),
  },
}));

const formatTimeStamp = timeStamp =>
  new Date(timeStamp).toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    minute: 'numeric',
    hour: 'numeric',
    timeZoneName: 'short',
  });

export const AvailRow = ({avail}) => {
  const ALID_SEARCH_TYPE = 'ALID';
  const dispatch = useDispatch();
  const {params: storeParams} = useSelector(state => state?.results);

  const [comparingPreviousAvail, setComparingPreviousAvail] = useState();
  const [comparing, setComparing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [relatedError, setRelatedError] = useState();

  const handleFetchOldVersion = searchType => {
    const {baseUrl, method, params, path} = serviceConfig[`AVAIL_COMPARE_BY_${searchType}`];

    if (!comparingPreviousAvail) {
      dispatch(setLoading(true));

      setIsFetching(true);
      axios[method.toLowerCase()](`${baseUrl}${path}`, {
        params: {
          ...params,
          recordId: avail.id,
          alid: avail.alid,
        },
      })
        .then(({data}) => {
          const {avails} = data;

          setComparingPreviousAvail(avails[0]);
          setComparing(true);
        })
        .catch(err => {
          const {statusText} = err?.response;
          dispatch(
            handleResultsError({
              errorMessage: err.message,
              errorStatus: statusText,
              loading: false,
            })
          );
        })
        .finally(() => {
          dispatch(setLoading(false));
          setIsFetching(false);
        });
    }
  };

  const handleCompareToggle = searchType => evt => {
    evt.preventDefault();
    evt.stopPropagation();

    if (!comparing) {
      if (!comparingPreviousAvail) {
        handleFetchOldVersion(searchType);
      } else {
        setComparing(true);
      }
    } else {
      setComparing(false);
    }
  };

  const handleFindAllAvailVersions = evt => {
    evt.preventDefault();
    evt.stopPropagation();

    dispatch(
      findAvailBySimpleSearchResults({
        endpoint: AVAIL_ENDPOINT,
        searchType: 'alid',
        valueToFind: avail.alid,
        storeParams,
      })
    );
  };

  const handleFetchError = evt => {
    evt.preventDefault();
    evt.stopPropagation();

    if (!relatedError) {
      setIsFetching(true);
      axios.get(`/sandwich/availerrors/alid?limit=1&page=0&alid=${avail.alid}`).then(({data}) => {
        const {availErrors} = data;
        setRelatedError(availErrors[0]);
        setIsFetching(false);
      });
    }
  };

  const classes = useAvailRowStyles();
  const ingestedDate = avail.timeStamp && formatTimeStamp(avail.timeStamp);

  return (
    <TableRow>
      <TableCell style={{padding: 0}}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <div className={classes.resultRowSummary}>
              <p style={{width: '50%', margin: '0'}}>
                <strong>ALID:</strong>&nbsp;{avail.alid} &nbsp;
                <ActionButton
                  handleClick={handleFindAllAvailVersions}
                  icon={Search}
                  toolTipTitle="Find all avail versions"
                />
              </p>

              <p style={{width: '30%'}}>
                {ingestedDate && ingestedDate !== '' ? (
                  <>
                    <strong>Ingested at:</strong>&nbsp;{`${ingestedDate}`}
                  </>
                ) : (
                  <strong>No ingestion date found:</strong>
                )}
              </p>

              <div className={classes.availStatusSection}>
                {avail.isNew ? (
                  <Chip className={`${classes.newAvailChip} ${classes.baseChip}`} label="New Avail" />
                ) : (
                  <Chip className={classes.baseChip} label="Updated Avail" />
                )}
                {avail.hasError ? (
                  <Chip className={`${classes.baseChip} ${classes.availHasErrorChip}`} label="Error" />
                ) : null}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails style={{display: 'block', width: '100%'}}>
            {avail.hasError || !avail.isNew ? (
              <div className={classes.actionsWrapper}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <p>
                    <strong>Avail Actions</strong>
                  </p>
                </div>
                <div className={classes.actionButtonsWrapper}>
                  {comparing && <strong style={{lineHeight: '28px'}}>Comparing from old avail</strong>}
                  {isFetching && <CircularProgress className={classes.actionsLoadingSpinner} thickness={6} />}
                  {!avail.isNew && (
                    <Button
                      color="primary"
                      disabled={isFetching}
                      variant="contained"
                      onClick={handleCompareToggle(ALID_SEARCH_TYPE)}
                    >
                      {!comparing ? 'Compare with last version' : 'Stop Comparing'}
                    </Button>
                  )}
                  {avail.hasError && (
                    <Button color="secondary" variant="contained" onClick={handleFetchError}>
                      Find Error
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
            {relatedError && (
              <div className={classes.relatedErrorWrapper}>
                <p>
                  <strong>Error found:</strong>
                </p>
                <ErrorResult error={relatedError} />
              </div>
            )}
            <AvailResult avail={avail} previousAvailVersion={comparing && comparingPreviousAvail} />
          </AccordionDetails>
        </Accordion>
      </TableCell>
    </TableRow>
  );
};

AvailRow.propTypes = {
  avail: PropTypes.object,
};

const useErrorRowStyles = makeStyles(theme => ({
  errorRowSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    justifyContent: 'space-between',
  },
  errorRowDetails: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
  },
  actionsWrapper: {
    alignItems: 'center',
    background: 'lightgray',
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(5),
  },
  actionButtonsWrapper: {
    gap: theme.spacing(1),
    display: 'flex',
    height: '100%',
  },
  actionsLoadingSpinner: {
    width: `${theme.spacing(3)}px !important`,
    height: `${theme.spacing(3)}px !important`,
  },
  availStatusSection: {
    width: '20%',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'right',
    gap: theme.spacing(0.5),
  },
  relatedAvailSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    justifyContent: 'left',
  },
}));

export const ErrorRow = ({error}) => {
  const classes = useErrorRowStyles();

  const [isFetching, setIsFetching] = useState();
  const [relatedAvail, setRelatedAvail] = useState();

  const handleFetchRelatedAvail = () => {
    if (!relatedAvail) {
      setIsFetching(true);
      axios.get(`/sandwich/availmessages/alid?limit=20000000&page=0&alid=${error.alid}`).then(({data}) => {
        const {avails} = data;

        setRelatedAvail(avails[0]);
        setIsFetching(false);
      });
    }
  };

  const triggeredDate = error.timeStamp && formatTimeStamp(error.timeStamp);

  return (
    <TableRow>
      <TableCell style={{padding: 0}}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <div className={classes.errorRowSummary}>
              <p style={{width: '40%'}}>
                {error.alid ? (
                  <>
                    <strong>ALID:</strong>&nbsp;{error.alid}
                  </>
                ) : (
                  <>Not avail related</>
                )}
              </p>

              <div style={{width: '35%'}}>
                <strong>Triggered on:</strong>&nbsp;{triggeredDate}
              </div>
              <div style={{width: '15%'}}>
                <strong>System:</strong>&nbsp;{error.system}
              </div>
              <div style={{width: '10%'}}>
                <strong>Severity:</strong>&nbsp;{error.severity}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.errorRowDetails}>
            <div className={classes.actionsWrapper}>
              <div style={{display: 'flex', alignItems: 'center'}}>
                <p>
                  <strong>Error Actions</strong>
                </p>
              </div>
              <div className={classes.actionButtonsWrapper}>
                {!!relatedAvail && <strong style={{lineHeight: '28px'}}>Found Avail</strong>}
                {isFetching && <CircularProgress className={classes.actionsLoadingSpinner} thickness={6} />}
                {
                  <Button color="primary" variant="contained" onClick={handleFetchRelatedAvail}>
                    find related avail
                  </Button>
                }
              </div>
            </div>
            <ErrorResult error={error} />

            {!!relatedAvail && (
              <div>
                <h3>Found Related Avail:</h3>
                <Table style={{tableLayout: 'fixed', width: '100%'}}>
                  <TableHead />
                  <TableBody>
                    <AvailRow avail={relatedAvail} />
                  </TableBody>
                </Table>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      </TableCell>
    </TableRow>
  );
};

ErrorRow.propTypes = {
  error: PropTypes.object,
};
